import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchDocumentsApi } from "../../../api/client";
import { documentContentUrlKeys } from "./documentsAndMessagesKeys";

/**
 * Shared queryOptions for a single document's presigned content URL, backed by
 * `WorkbenchDocsApi.getDocumentContentUrl` → `GET /api/v1/workbench/applications/{appId}/docs/{docId}/content-url`.
 *
 * Presigned URLs are short-lived, so this resolves fresh on demand rather than
 * caching: `staleTime: 0` and a short `gcTime`.
 * @param {string} appId - The approval / application id.
 * @param {string} documentId - The document id.
 * @param {boolean} enabled - Whether the query should run (ids present + session active).
 * @returns {ReturnType<typeof queryOptions>} Typed options resolving to the content URL string.
 */
export function documentContentUrlQuery(appId: string, documentId: string, enabled: boolean) {
  return queryOptions({
    queryKey: documentContentUrlKeys.detail({ appId, docId: documentId }),
    queryFn: async ({ signal }): Promise<string> => {
      const response = await workbenchDocumentsApi.getDocumentContentUrl(appId, documentId, { signal });
      const contentUrl = response.data.downloadUrl;
      if (!contentUrl) {
        throw new Error("The document content URL was empty.");
      }
      return contentUrl;
    },
    staleTime: 0,
    gcTime: 30_000,
    enabled: enabled && !!appId && !!documentId,
    retry: false,
    // Presigned URLs expire; re-resolve a fresh one when the analyst returns to
    // the viewer window or the network reconnects (the app-wide QueryClient
    // disables focus refetch, so opt back in here).
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Resolves a fresh presigned content URL for a document. Disabled until both
 * ids are present and `enabled` (e.g. the session is active), so the viewer can
 * render an empty/loading state instead of firing a doomed request.
 * @param {string} appId - The approval / application id.
 * @param {string} documentId - The document id.
 * @param {boolean} enabled - Whether the query should run.
 * @returns {ReturnType<typeof useQuery<string>>} The TanStack Query result.
 */
export function useDocumentContentUrl(appId: string, documentId: string, enabled: boolean) {
  return useQuery(documentContentUrlQuery(appId, documentId, enabled));
}
